/**
 * Mobile header menu — drill-down model.
 *
 * Levels:
 *   L1  Top-level primary nav (slides down from top with the panel itself).
 *   L2  Depth-1 children of the active L1 item (push transition: L1 slides
 *       out left while the L2 screen slides in from the right; a "Back to
 *       main menu" button returns).
 *   L3  Depth-2 children of an L2 item (inline accordion; only one open at
 *       a time within the active L2 screen).
 *
 * Behaviour:
 *   - Hamburger toggles `body.mobile-menu-open` → panel slides down (CSS).
 *   - Tapping an L1 parent with children sets `is-active` on that <li>
 *     (renders its L2 screen at left:100% of the nav) and adds
 *     `mobile-submenu-open` to <body> → the nav translates -100%, pushing
 *     L1 out and the L2 screen in; the back button appears.
 *   - Back button removes `mobile-submenu-open` → nav slides back; the
 *     `is-active` class is cleared AFTER the transition so the L2 screen
 *     stays visible while it slides out.
 *   - Inside the L2 screen, tapping an L3 parent toggles `is-expanded` on
 *     its <li>; siblings with `is-expanded` are cleared first.
 *   - Tapping a leaf link closes the whole panel.
 *   - Resizing back to desktop tears the panel down cleanly.
 */

import { getLenisInstance } from '../../core/scroll-manager';

const MOBILE_BREAKPOINT = 1080;

export function initializeMobileMenu() {
    const header = document.getElementById('header');
    if (!header) return;

    const panel = header.querySelector('.header__menu-panel');
    const toggles = header.querySelectorAll('.header__menu-toggle');
    const backBtn = header.querySelector('.header__back');
    if (!panel || !toggles.length) return;

    const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT;

    let submenuCloseTimer = null;

    const closeAllAccordions = () => {
        panel.querySelectorAll('li.is-expanded').forEach(li => {
            li.classList.remove('is-expanded');
            const link = li.querySelector(':scope > a');
            if (link) link.setAttribute('aria-expanded', 'false');
        });
    };

    /**
     * Return from L2 to L1. The push transition runs on the nav; the active
     * L2 screen must stay rendered (`is-active`) until it finishes, so
     * deactivation is deferred unless `immediate` is set (panel teardown).
     *
     * @param {boolean} immediate Deactivate without waiting for the transition.
     */
    const closeSubmenu = (immediate = false) => {
        document.body.classList.remove('mobile-submenu-open');
        if (backBtn) backBtn.hidden = true;
        panel.scrollTop = 0;
        panel.querySelectorAll('.header__primary > ul > li.menu-item-has-children > a[aria-expanded="true"]')
            .forEach(link => link.setAttribute('aria-expanded', 'false'));

        const deactivate = () => {
            panel.querySelectorAll('.header__primary > ul > li.is-active')
                .forEach(li => li.classList.remove('is-active'));
            closeAllAccordions();
        };

        clearTimeout(submenuCloseTimer);
        if (immediate) {
            deactivate();
        } else {
            submenuCloseTimer = setTimeout(deactivate, 400);
        }
    };

    const closePanel = () => {
        document.body.classList.remove('mobile-menu-open');
        toggles.forEach(t => {
            t.setAttribute('aria-expanded', 'false');
            t.classList.remove('is-active');
        });
        closeSubmenu(true);
        const lenis = getLenisInstance();
        if (lenis) lenis.start();
    };

    const openPanel = () => {
        if (!isMobile()) return;
        document.body.classList.add('mobile-menu-open');
        toggles.forEach(t => {
            t.setAttribute('aria-expanded', 'true');
            t.classList.add('is-active');
        });
        const lenis = getLenisInstance();
        if (lenis) lenis.stop();
    };

    toggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
            e.preventDefault();
            if (document.body.classList.contains('mobile-menu-open')) {
                closePanel();
            } else {
                openPanel();
            }
        });
    });

    // L1 → L2 drill-down: top-level parents only
    const l1Triggers = panel.querySelectorAll(
        '.header__primary > ul > li.menu-item-has-children > a'
    );
    l1Triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
            if (!isMobile()) return;
            e.preventDefault();
            e.stopPropagation();

            // Clear any other top-level active state and accordions before activating this one.
            clearTimeout(submenuCloseTimer);
            panel.querySelectorAll('.header__primary > ul > li.is-active')
                .forEach(li => li.classList.remove('is-active'));
            closeAllAccordions();

            trigger.parentElement.classList.add('is-active');
            trigger.setAttribute('aria-expanded', 'true');
            document.body.classList.add('mobile-submenu-open');
            if (backBtn) backBtn.hidden = false;
            // Reset scroll so the new screen starts at the top.
            panel.scrollTop = 0;
        });
    });

    // L2 → L3 accordion: depth-1 parents inside the top-level sub-menu
    const l3Triggers = panel.querySelectorAll(
        '.header__primary > ul > li > .sub-menu > ul > li.menu-item-has-children > a'
    );
    l3Triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
            if (!isMobile()) return;
            e.preventDefault();
            e.stopPropagation();

            const li = trigger.parentElement;
            const wasOpen = li.classList.contains('is-expanded');
            // Only one accordion open at a time within this L2 screen.
            const sibList = li.parentElement;
            sibList.querySelectorAll(':scope > li.is-expanded').forEach(s => {
                if (s === li) return;
                s.classList.remove('is-expanded');
                const sLink = s.querySelector(':scope > a');
                if (sLink) sLink.setAttribute('aria-expanded', 'false');
            });
            li.classList.toggle('is-expanded', !wasOpen);
            trigger.setAttribute('aria-expanded', !wasOpen ? 'true' : 'false');
        });
    });

    // Back button: leave L2, return to L1.
    if (backBtn) {
        backBtn.addEventListener('click', (e) => {
            e.preventDefault();
            closeSubmenu();
        });
    }

    /**
     * Close the panel with all menu transitions suppressed for one frame —
     * used on page navigations so the teardown doesn't visibly animate.
     */
    const closePanelInstant = () => {
        document.body.classList.add('mobile-menu-no-transition');
        closePanel();
        requestAnimationFrame(() => requestAnimationFrame(() => {
            document.body.classList.remove('mobile-menu-no-transition');
        }));
    };

    // Leaf links: only same-page anchor links need the panel closed (the
    // page doesn't reload). Real navigations leave the menu untouched —
    // the barba-leave-start handler tears it down instantly instead.
    panel.querySelectorAll('li:not(.menu-item-has-children) > a').forEach(link => {
        link.addEventListener('click', () => {
            if (!isMobile()) return;
            const samePageAnchor = link.hash
                && link.host === location.host
                && link.pathname === location.pathname;
            if (samePageAnchor) closePanel();
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (document.body.classList.contains('mobile-submenu-open')) {
            closeSubmenu();
        } else if (document.body.classList.contains('mobile-menu-open')) {
            closePanel();
        }
    });

    document.addEventListener('barba-leave-start', closePanelInstant);

    window.addEventListener('resize', () => {
        if (!isMobile() && document.body.classList.contains('mobile-menu-open')) {
            closePanel();
        }
    });
}
